import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import { getClaims } from "./get-claims";
import { getSupabase } from "./utils";

import type { Database, DatabaseWithOptions } from "../../../__definitions__/database-definitions";
import type { SupabaseHandlerArg, SupabaseSchemaName } from "./types";
import type { DependencyList } from "react";

export type SupabaseCallback<T extends unknown[], U> = (...args: T) => Promise<U | undefined>;

/**
 * Create Event handler callback to make best-effort Supabase save if still authenticated.
 * n.b. dose not work well for loading data. Initial renders will skip callback as Auth0 is still initializing.
 */
export function useSupabaseCallback<
  T extends unknown[],
  U,
  SchemaName extends SupabaseSchemaName<Database> = "public",
>(
  callback: (arg0: SupabaseHandlerArg<SchemaName>, ...args: T) => Promise<U>,
  deps: DependencyList,
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  schema: SchemaName = "public" as SchemaName,
): SupabaseCallback<T, U> {
  const { loginWithRedirect, getAccessTokenSilently } = useAuth0();
  return useCallback(
    async (...args) => {
      const abortController = new AbortController();
      let token: string | null = null;
      for (let i = 0; i < 10; i++) {
        try {
          token = await getAccessTokenSilently();
          break;
        } catch {
          // ignore
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      if (!token) {
        console.error("Callback failed to get token silently after multiple attempts");
        void loginWithRedirect(
          window.location.pathname.includes("/logout")
            ? {}
            : {
                appState: {
                  returnTo: `${window.location.pathname}${window.location.search}`,
                },
              },
        );
        return;
      }
      const user = getClaims(token);
      return await callback(
        {
          supabase: getSupabase<DatabaseWithOptions, SchemaName>(
            user["http://changeengine.com/supabase_url"],
            user["http://changeengine.com/supabase_anon_key"],
            token,
            schema,
            abortController,
          ),
          account_id: user["http://changeengine.com/account_id"],
          org_id: user.org_id,
          token,
          abortController,
        },
        ...args,
      );
    },
    [schema, getAccessTokenSilently, loginWithRedirect, ...deps],
  );
}
